// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@guestsupply/App/Components/Notify";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { HasCartContext, withCart } from "@insite/client-framework/Components/CartContext";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderApprovalDetailsActionButtons, {
    OrderApprovalDetailsActionButtonsStyles,
} from "@insite/content-library/Widgets/OrderApprovalDetails/OrderApprovalDetailsActionButtons";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// XNG-Changes: display site message when logged in with the wrong customer
const mapStateToProps = (state: ApplicationState) => ({
    currentBillTo: getCurrentBillToState(state)?.value,
    orderApprovalListPageUrl: getPageLinkByPageType(state, "OrderApprovalListPage")?.url,
    changeCustomerPageUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
});

export interface OrderApprovalDetailsHeaderStyles {
    headerGridContainer?: GridContainerProps;
    title?: TypographyProps;
    orderNumberText?: TypographyProps;
    titleGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    orderApprovalDetailsActionButtonsStyles?: OrderApprovalDetailsActionButtonsStyles;
    // XNG-Changes
    wrongCustomerGridItem?: GridItemProps;
    wrongCustomerWarning?: InjectableCss;
}

// Check OrderApprovalDetailsHeaderExtensions.ts
export const headerStyles: OrderApprovalDetailsHeaderStyles = {
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: [2, 2, 2, 6, 6],
    },
    titleGridItem: {
        width: [10, 10, 10, 6, 6],
    },
    title: {
        variant: "h2",
        as: "p",
        css: css`
            @media print {
                font-size: 11px;
            }
        `,
    },
};

const styles = headerStyles;

type Props = WidgetProps & HasCartContext & ReturnType<typeof mapStateToProps>;

const OrderApprovalDetailsHeader = ({
    cart,
    currentBillTo,
    orderApprovalListPageUrl,
    changeCustomerPageUrl,
}: Props) => {
    if (!cart || !cart.billTo || !cart.shipTo || !currentBillTo) {
        return null;
    }

    // XNG-Changes: display site message when logged in with the wrong customer
    const wrongCustomer = currentBillTo?.customerNumber !== cart.billTo.customerNumber;

    return (
        <GridContainer {...styles.headerGridContainer}>
            <GridItem {...styles.titleGridItem}>
                {createWidgetElement("Basic/PageTitle", {
                    fields: {
                        overrideTitle: translate("Order Approval Details"),
                        showGoBack: true,
                        goBackTitle: translate("Return to Order Approval"),
                        goBackLink: orderApprovalListPageUrl,
                    },
                })}
            </GridItem>
            {/* XNG-Changes: display site message when logged in with the wrong customer */}
            {!wrongCustomer && (
                <GridItem {...styles.buttonGridItem}>
                    <OrderApprovalDetailsActionButtons
                        cart={cart}
                        extendedStyles={styles.orderApprovalDetailsActionButtonsStyles}
                    />
                </GridItem>
            )}
            {/* XNG-Changes: display site message when logged in with the wrong customer */}
            {wrongCustomer && (
                <GridItem {...styles.wrongCustomerGridItem}>
                    <Notify type="warning" {...styles.wrongCustomerWarning}>
                        <Typography>
                            {translate("This order was placed for customer ")}
                            <strong>{cart.billTo?.customerNumber}</strong>
                            {translate(", but you are currently logged in as customer ")}
                            <strong>{currentBillTo?.customerNumber}</strong>
                            {translate(". Please visit the ")}
                            <Link href={changeCustomerPageUrl}>{translate("Change Customer")}</Link>
                            {translate(" screen to select the correct customer before proceeding.")}
                        </Typography>
                    </Notify>
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCart(OrderApprovalDetailsHeader)),
    definition: {
        displayName: "Page Header",
        allowedContexts: ["OrderApprovalDetailsPage"],
        group: "Order Approval Details",
    },
};

export default widgetModule;
